import { useCurrentUser } from '@beholos/holoscommon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { SidebarContent } from './SideBar';
import {
  getUserNotifications,
  updateUserNotification,
  type PaginatedResponse,
  type UserNotification,
  getDashboardDesignValues,
} from '../../services/user.service';
import { BASE_URL } from '../../services/constants';
import { logout } from '../../services/auth.service';

export default function Header() {
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [uiVisible, setUiVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const notificationsPanelRef = useRef<HTMLDivElement | null>(null);
  const notificationsButtonRef = useRef<HTMLButtonElement | null>(null);
  const [notificationsData, setNotificationsData] = useState<PaginatedResponse<UserNotification>>({
    docs: [],
    limit: 20,
    page: 1,
    totalDocs: 0,
    totalPages: 0,
    hasPrevPage: false,
    hasNextPage: false,
    pagingCounter: 0,
    prevPage: null,
    nextPage: null,
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);
  const [hasUnreadGlobal, setHasUnreadGlobal] = useState(false);
  const pendingDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDismissIdRef = useRef<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [designValues, setDesignValues] = useState<any>(null);
  const { user } = useCurrentUser();
  
  // Fade in UI on first mount
  useEffect(() => {
    const t = setTimeout(() => setUiVisible(true), 0);
    return () => clearTimeout(t);
  }, []);
  const profilePanelRef = useRef<HTMLDivElement | null>(null);
  const profileButtonRef = useRef<HTMLButtonElement | null>(null);

  // keeps track of unread within the currently loaded page (not used for badge)
  // const hasUnread = useMemo(() => notificationsData.docs.some((n) => !n.read), [notificationsData.docs]);

  useEffect(() => {
    (async () => {
      try {
        const values = await getDashboardDesignValues();
        // eslint-disable-next-line no-console
        console.log('Dashboard design values:', values);
        setDesignValues(values);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const fetchNotifications = useCallback(async (page: number, autoMarkRead: boolean = true) => {
    try {
      setNotificationsError(null);
      setLoadingNotifications(true);
      const data = await getUserNotifications(page);
      setNotificationsData(data);
      setHasUnreadGlobal(data.docs.some((n) => !n.read));

      // Auto-mark visible notifications as read (legacy-like) only when opening panel
      if (autoMarkRead) {
        const unread = data.docs.filter((n) => !n.read);
        if (unread.length > 0) {
          await Promise.all(
            unread.map((n) => updateUserNotification(n.id, { read: true }))
          );
          // Refetch to reflect read state
          const refreshed = await getUserNotifications(page);
          setNotificationsData(refreshed);
          setHasUnreadGlobal(refreshed.docs.some((n) => !n.read));
        }
      }
    } catch (e: any) {
      console.error(e);
      setNotificationsError('No se pudieron cargar las notificaciones.');
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const handleToggleNotifications = async () => {
    setNotificationsOpen((o) => !o);
  };

  useEffect(() => {
    if (notificationsOpen) {
      fetchNotifications(notificationsPage, true);
    }
  }, [notificationsOpen, notificationsPage, fetchNotifications]);

  // Close notifications panel when clicking outside
  useEffect(() => {
    if (!notificationsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const panel = notificationsPanelRef.current;
      const button = notificationsButtonRef.current;
      const target = event.target as Node | null;
      if (!target) return;
      if (panel && panel.contains(target)) return;
      if (button && button.contains(target)) return;
      setNotificationsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationsOpen]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!profileOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const panel = profilePanelRef.current;
      const button = profileButtonRef.current;
      const target = event.target as Node | null;
      if (!target) return;
      if (panel && panel.contains(target)) return;
      if (button && button.contains(target)) return;
      setProfileOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  const handleMarkRead = async (notification: UserNotification, read: boolean) => {
    try {
      await updateUserNotification(notification.id, { read });
      if (!read) {
        // When marking as unread, close the panel and keep the bell marked
        setNotificationsOpen(false);
        setHasUnreadGlobal(true);
      }
      await fetchNotifications(notificationsPage, false);
    } catch (e) {
      setNotificationsError('No se pudo actualizar la notificación.');
    }
  };

  const handleDismiss = async (notification: UserNotification) => {
    // Optimistic UI: remove locally and offer undo; commit to server after delay
    setNotificationsData((prev) => ({ ...prev, docs: prev.docs.filter((n) => n.id !== notification.id) }));
    setToastMessage('Acabas de descartar esta notificación. ¿Deshacer?');
    setToastVisible(true);
    pendingDismissIdRef.current = notification.id;

    if (pendingDismissTimerRef.current) clearTimeout(pendingDismissTimerRef.current);
    pendingDismissTimerRef.current = setTimeout(async () => {
      try {
        if (pendingDismissIdRef.current) {
          await updateUserNotification(pendingDismissIdRef.current, { dismissed: true });
          await fetchNotifications(notificationsPage, false);
        }
      } catch (e) {
        setNotificationsError('No se pudo descartar la notificación.');
      } finally {
        pendingDismissIdRef.current = null;
        setToastVisible(false);
      }
    }, 5000);
  };

  const handleUndoDismiss = async () => {
    if (pendingDismissTimerRef.current) clearTimeout(pendingDismissTimerRef.current);
    pendingDismissTimerRef.current = null;
    pendingDismissIdRef.current = null;
    setToastVisible(false);
    // Refetch to restore the list without committing dismissal
    await fetchNotifications(notificationsPage, false);
  };

  // Smoothly fade out header when logging out
  const handleLogout = async () => {
    try {
      setIsFadingOut(true);
      setUiVisible(false);
      // Allow transition to play before triggering logout flow
      setTimeout(() => {
        logout();
      }, 250);
    } catch {
      logout();
    }
  };

  // Background poll to check for unread notifications and animate bell
  const checkUnread = useCallback(async () => {
    try {
      const data = await getUserNotifications(1);
      setHasUnreadGlobal(data.docs.some((n) => !n.read));
    } catch (e) {
      // ignore polling errors
    }
  }, []);

  useEffect(() => {
    checkUnread();
    const id = setInterval(checkUnread, 30000);
    return () => clearInterval(id);
  }, [checkUnread]);

  return (
    <>
    <header className={`bg-holos-light sticky top-0 z-50 shadow-md`}>
      <div className={`flex items-center h-20 px-4 md:px-6 transition-opacity duration-300 ${uiVisible && !isFadingOut ? 'opacity-100' : 'opacity-0'}`}>
        <button
          className="md:hidden mr-4 cursor-pointer"
          aria-label="Open menu"
          onClick={() => setMobileMenuOpen(true)}
        >
          <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <h1 className="flex-grow md:flex-grow-0 flex items-center">
          {designValues?.studioImage && (
            <img
              src={`https://image.beholos.com/${designValues.studioImage}`}
              alt="Corporate Logo"
              className="hidden md:inline-flex mr-3 h-9 w-auto"
            />
          )}
          <a href={BASE_URL + '/'} className="flex items-center h-full">
            <img
              src="https://cache.beholos.com/img/holos/logos/holos-logo-final-version-v2.svg"
              alt="Holos Logo"
              className="hidden md:block h-8 w-auto"
            />
            <img
              src="https://cache.beholos.com/img/holos/logos/logo-black-short.svg"
              alt="Holos Logo"
              className="md:hidden h-8 w-auto"
            />
          </a>
        </h1>



        <div className="relative mr-4 flex flex-grow justify-end">
          <button
            ref={notificationsButtonRef}
            onClick={handleToggleNotifications}
            className="relative p-2 hover:bg-holos-dark/10 rounded-full cursor-pointer"
            aria-label="Notifications"
          >
            <Bell className={`w-5 h-5 text-gray-800 ${hasUnreadGlobal ? 'bell-shake' : ''}`} />
            {hasUnreadGlobal && (
              <span className="absolute -top-0.5 -right-0.5 block h-2 w-2 rounded-full bg-red-500" />
            )}
          </button>

          {notificationsOpen && (
            <div ref={notificationsPanelRef} className="fixed inset-x-4 top-24 bg-gray-700 shadow-lg rounded-lg text-white text-xs max-h-96 overflow-hidden z-50 md:absolute md:inset-x-auto md:top-auto md:right-0 md:mt-10 md:w-96">
              <div className="text-center font-semibold text-white py-2 border-b">Notificaciones</div>

              <div className="max-h-72 overflow-y-auto">
                {loadingNotifications && (
                  <div className="p-4 text-center text-gray-300">Cargando...</div>
                )}
                {!loadingNotifications && notificationsError && (
                  <div className="p-3 text-red-300 text-center">{notificationsError}</div>
                )}
                {!loadingNotifications && !notificationsError && notificationsData.docs.length === 0 && (
                  <div className="p-4 text-center text-gray-300">No tienes notificaciones.</div>
                )}
                {!loadingNotifications && !notificationsError && notificationsData.docs.map((n) => (
                  <div key={n.id} className={`flex px-3 py-2 border-t ${n.read ? 'bg-gray-700' : 'bg-gray-600/60'} hover:bg-holos-light/30`}>
                    <div className="flex-shrink-0 flex items-start pt-1 pr-3">
                      <div className={`h-3 w-3 rounded-full ${n.read ? 'bg-gray-500' : 'bg-holos-dark'}`} />
                    </div>
                    <div className="flex-grow">
                      <p className="leading-snug mb-0.5 font-medium">{n.title}</p>
                      <p className="leading-snug mb-0.5">{n.message} {n.link && (
                        <a href={n.link} className="ml-1 text-holos-dark underline">{n.linkText || 'Ver más'}</a>
                      )}</p>
                      <div className="text-[10px] text-gray-300 flex items-center gap-2 mt-1">
                        <button onClick={() => handleMarkRead(n, !n.read)} className="underline hover:text-white cursor-pointer">
                          {n.read ? 'Marcar como no leído' : 'Marcar como leído'}
                        </button>
                        <span>•</span>
                        <button onClick={() => handleDismiss(n)} className="underline hover:text-white cursor-pointer">Descartar</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between p-2 border-t border-gray-600 text-[11px]">
                <button
                  disabled={!notificationsData.hasPrevPage || loadingNotifications}
                  onClick={() => setNotificationsPage((p) => Math.max(1, (notificationsData.prevPage || p - 1)))}
                  className={`px-2 py-1 rounded ${notificationsData.hasPrevPage ? 'hover:bg-holos-light/30 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                >
                  Anterior
                </button>
                <div className="text-gray-300">Página {notificationsData.page} de {Math.max(1, notificationsData.totalPages)}</div>
                <button
                  disabled={!notificationsData.hasNextPage || loadingNotifications}
                  onClick={() => setNotificationsPage((p) => (notificationsData.nextPage || p + 1))}
                  className={`px-2 py-1 rounded ${notificationsData.hasNextPage ? 'hover:bg-holos-light/30 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}

          {toastVisible && (
            <div className="fixed bottom-4 right-4 bg-gray-800 text-white text-sm rounded shadow-lg px-4 py-3 z-[100] flex items-center gap-3">
              <span>{toastMessage}</span>
              <button onClick={handleUndoDismiss} className="underline hover:text-holos cursor-pointer">Deshacer</button>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            ref={profileButtonRef}
            onClick={() => setProfileOpen((p) => !p)}
            className="flex items-center py-2 px-3 hover:bg-holos-dark/10 rounded-lg cursor-pointer"
          >
            <img
              src="https://image.beholos.com/img/holos/general/ic-default-profile.webp"
              alt="Profile"
              className="h-8 w-8 rounded-full object-cover"
            />
            <span className="ml-2 font-juana text-gray-900 hidden sm:inline-block">{user?.firstname}</span>
            <svg
              className="w-4 h-4 ml-1 text-gray-800"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {profileOpen && (
            <div ref={profilePanelRef} className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-lg overflow-hidden">
              <a
                href={`${BASE_URL + '/'}`}
                className="block px-4 py-2 text-gray-800 hover:bg-holos-light/30 cursor-pointer"
              >
                INICIO
              </a>
              <a
                onClick={handleLogout}
                className="block px-4 py-2 text-gray-800 hover:bg-holos-light/30 cursor-pointer"
              >
                CERRAR SESIÓN
              </a>
            </div>
          )}
        </div>
      </div>
    </header>
    {/* Mobile drawer */}
    <div className={`fixed inset-0 z-[60] md:hidden ${mobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`} aria-hidden={!mobileMenuOpen}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      {/* Drawer */}
      <div className={`absolute top-0 left-0 h-full w-80 max-w-[85%] bg-white shadow-xl transform transition-transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between px-4 h-14 border-b">
          <span className="font-semibold">Menú</span>
          <button aria-label="Close menu" onClick={() => setMobileMenuOpen(false)} className="cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="h-[calc(100%-56px)] overflow-y-auto">
          <SidebarContent user={user} onNavigate={() => setMobileMenuOpen(false)} />
        </div>
      </div>
    </div>
    </>
  );
}